import { Button, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Radio, RadioGroup, TextField } from "@material-ui/core";
import _ from "lodash";
import React, { Component } from "react";
import styled from "styled-components";

import { WorkspaceResourceType } from "common/interfaces";
import WorkspaceController, { getWorkspaceResourcesTags } from "js/controllers/WorkspaceController";
import { Slide, getSlideDataStateFromAttributes } from "js/core/models/slide";
import DummyAdapter from "js/core/storage/dummyAdapter";
import { htmlToText, sanitizeHtmlText } from "js/core/utilities/htmlTextHelpers";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import { BeautifulDialog, ShowDialog, ShowErrorDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap10 } from "js/react/components/Gap";
import TagsList from "js/react/components/TagsList";
import { Field } from "js/react/components/UiComponents";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";

const RadioLabel = styled.div`
  font-size: 14px;
  margin-left: 31px;
  margin-bottom: 20px;
  color: #666;
  margin-top: -5px;
`;

const RadioButtonControl = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    font-size: 18px;
    font-weight: 600;
  }
`;

export class CreateSharedSlideDialog extends Component {
    state = {
        name: "",
        description: "",
        tags: [],
        slideType: TeamResourceTypes.SLIDE_TEMPLATES,
        fetching: false
    }

    constructor(props) {
        super(props);
    }

    getSlideName = slide => {
        const slideDataState = getSlideDataStateFromAttributes(slide.attributes);
        if (!slideDataState?.elements.primary) {
            return "";
        }

        const state = slide.attributes.states[0];
        const templateId = slide.get("template_id");
        const primaryText = this.extractTextByPriority(state, ["primary"]);

        // Special handling for different slide types
        if (templateId === "headline2" || templateId === "section" || templateId === "headline") {
            const blocks = state.primary?.text?.blocks;

            if (Array.isArray(blocks)) {
                const priorityIds = ["headline", "heading", "title"];

                for (const id of priorityIds) {
                    //section has id but headline does not, so need to check textStyle
                    const block = blocks.find(b => b.textStyle === id);
                    const html = block?.html;

                    if (html) {
                        const text = htmlToText(html).trim();
                        if (text) {
                            return text;
                        }
                    }
                }
            }
            return "";
        } else if (templateId === "collage") {
            return this.extractTextByPriority(state, ["header"]);
        } else if (templateId === "title") {
            return this.extractTextByPriority(state, ["primary"]);
        } else if (templateId === "contactus") {
            const primaryItems = state.primary?.items;

            if (Array.isArray(primaryItems)) {
                const contactHeader = primaryItems.find(item => item.type === "contact_header");
                const html = contactHeader?.text?.blocks?.[0]?.html;

                if (html) {
                    const text = htmlToText(html).trim();
                    if (text) {
                        return text;
                    }
                }
            }
            return t("Contact Us");
        } else {
            const headerText = this.extractTextByPriority(state, ["header"]);
            if (headerText) {
                return headerText;
            }

            // If no header return blank
            return "";
        }
    }

    //Helper method to extract text by priority from text blocks
    extractTextByPriority = (state, blockTypes) => {
        for (const blockType of blockTypes) {
            if (state[blockType] && state[blockType].text && state[blockType].text.blocks) {
                const block = state[blockType].text.blocks.find(b => b.html);
                if (block && block.html) {
                    const text = htmlToText(block.html);
                    if (text && text.trim()) {
                        return text.trim();
                    }
                }
            } else if (state[blockType] && state[blockType].text) {
                if (typeof state[blockType].text === "string") {
                    const text = state[blockType].text.trim();
                    if (text) {
                        return text;
                    }
                }
            }
        }

        return "";
    }

    componentDidMount() {
        const { resourceType, slide } = this.props;

        if (resourceType) {
            this.setState({
                slideType: resourceType
            });
        }
        // Auto-populate slide name based on slide content
        if (slide) {
            const extractedName = this.getSlideName(slide);
            if (extractedName) {
                this.setState({
                    name: extractedName
                });
            }
        }
    }

    handleAddTag = async () => {
        const existingTags = await getWorkspaceResourcesTags({ workspaceId: WorkspaceController.workspaceId, resourceTypes: [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE] });
        ShowDialog(AddTagsDialog, {
            activeTags: this.state.tags,
            existingTags: existingTags.map(tag => tag.tag),
            acceptCallback: newTags => {
                this.setState({
                    tags: newTags,
                });
            },
        });
    }

    handleRemoveTag = inputTag => {
        this.setState(({ tags }) => ({ tags: tags.filter(tag => tag !== inputTag) }));
    }

    handleConfirm = async () => {
        const { slide, presentation, onBeforeSaveSlide, closeDialog } = this.props;
        const { name, description, tags, slideType } = this.state;

        if (name.trim() === "") {
            return ShowErrorDialog({ title: t("Please enter a name for your team slide") });
        }

        try {
            this.setState({ fetching: true });

            let createdSlide;
            // If the slide is not saved or we are creating a Team Slide Template then will create and save a new slide
            if (slide.adapter instanceof DummyAdapter || slideType == TeamResourceTypes.SLIDE_TEMPLATES) {
                const slideAttributes = _.cloneDeep(slide.attributes);
                slideAttributes.isTeamSlideTemplate = slideType == TeamResourceTypes.SLIDE_TEMPLATES;
                delete slideAttributes.id;
                delete slideAttributes.presentationId;
                delete slideAttributes.libraryItemId;
                if (presentation) {
                    slideAttributes.presentationId = presentation.id;
                }
                createdSlide = new Slide(slideAttributes, { userId: app.user.id, autoSync: false });
                await createdSlide.load();
            } else {
                createdSlide = slide;
            }

            // Before committing a slide, perform any updates
            onBeforeSaveSlide && onBeforeSaveSlide(createdSlide);

            // if we are sharing an existing slide as a TeamSlide, trigger a model change so it switches a TeamSlide in the current presentation
            // If updating an existing slide then we'll wait for the changes to propagate to the slide
            const slideUpdatePromise = createdSlide === slide
                ? new Promise(resolve => createdSlide.once("change:libraryItemId", resolve))
                : Promise.resolve();

            const resource = {
                resourceId: createdSlide.id,
                resourceType: slideType == TeamResourceTypes.SLIDE_TEMPLATES ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                tags,
                folderIds: [],
                metadata: {
                    name,
                    description,
                    isDisabled: false,
                    contentModifiedAt: createdSlide.get("modifiedAt"),
                    usedByUsers: [app.user.id],
                    usedInPresentations: presentation ? [presentation.id] : [],
                    usedInPublicPresentations: presentation?.get("public") ? [presentation.id] : [],
                    ratedByUsers: [],
                    createdBy: app.user.id,
                    modifiedBy: app.user.id,
                }
            };

            await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });

            // Wait for the changes to propagate (if needed)
            await slideUpdatePromise;

            closeDialog();
        } catch (err) {
            this.setState({ fetching: false });
            ShowWarningDialog({ title: t("An error occurred while sharing your slide"), message: err });
        }
    }

    render() {
        const { closeDialog } = this.props;
        const { name, description, tags, fetching, slideType } = this.state;
        const selected = slideType === TeamResourceTypes.SLIDE_TEMPLATES ? TeamResourceTypes.SLIDE_TEMPLATES : TeamResourceTypes.SHARED_SLIDES;
        return (
            <BeautifulDialog maxWidth="md" fullWidth={false} closeDialog={closeDialog} preventClose={fetching}>
                <FetchingClickShield visible={fetching} backgroundColor="rgba(255, 255, 255, 0.7)" />
                <DialogTitle>{t("Share this slide with your team")}</DialogTitle>
                <DialogContent style={{ paddingLeft: 100, overflowY: "auto" }}>
                    <Field title={t("Slide Name")}>
                        <TextField
                            id="slide-name"
                            value={name}
                            placeholder={t("Enter a name for the team slide")}
                            fullWidth
                            onChange={event => this.setState({ name: sanitizeHtmlText(event.target.value) })}
                        />
                    </Field>
                    <Field title={t("Add a description or instructions for this slide (optional)")}>
                        <TextField multiline
                            fullWidth
                            value={description}
                            placeholder={t("Type a description here")}
                            onChange={event => this.setState({ description: sanitizeHtmlText(event.target.value) })}
                            inputProps={{
                                onBlur: event => this.setState({ description: event.target.value })
                            }}
                        />
                    </Field>
                    <Field title={t("Add Tags for easier searching (optional)")}>
                        <Gap10 />
                        <TagsList
                            tags={tags}
                            onRemove={this.handleRemoveTag}
                            onClick={this.handleAddTag}
                        />
                    </Field>

                    <Field title={t("How do you want to share this slide with your team?")}>
                        <Gap10 />
                        <FormControl>
                            <RadioGroup
                                value={selected}
                                defaultValue={TeamResourceTypes.SLIDE_TEMPLATES}
                                name="share-type"
                                style={{ marginLeft: 20 }}
                                onChange={event => this.setState({ slideType: event.target.value })}
                            >
                                <RadioButtonControl className="shared-template" control={<Radio />} label={t("Create Slide Template")} value={TeamResourceTypes.SLIDE_TEMPLATES} />
                                <RadioLabel>{t("An editable slide for your team to customize.")}</RadioLabel>
                                <RadioButtonControl className="shared-slides" control={<Radio />} label={t("Create Shared Slide")} value={TeamResourceTypes.SHARED_SLIDES} />
                                <RadioLabel>{t("A finished, uneditable slide. Team Librarians can edit Shared Slides and automatically update all instances.")}</RadioLabel>
                            </RadioGroup>
                        </FormControl>
                    </Field>
                </DialogContent>

                <DialogActions>
                    <Button id="cancelButton" onClick={closeDialog}>
                        {t("Cancel")}
                    </Button>
                    <Button id="okButton" onClick={this.handleConfirm} color="primary">
                        {t("Share Slide")}
                    </Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

export default CreateSharedSlideDialog;
